import { APITagAttributes, ArdoqId, APIEntityType } from '@ardoq/api-types';
import { TagInterfaceImplementation } from '@ardoq/tag-interface';
import { tags$ } from 'streams/tags/tags$';

const getAllTags = (): APITagAttributes[] => tags$.state.models;

const get = (tagId: ArdoqId): APITagAttributes | null =>
  tags$.state.byId[tagId] ?? null;

const getTagCount = (): number => tags$.state.models.length;

const getTagsForModel = (modelId: ArdoqId): { id: ArdoqId; name: string }[] =>
  getAllTags()
    .filter(
      tag =>
        tag.components?.includes(modelId) || tag.references?.includes(modelId)
    )
    .map(tag => ({ id: tag._id, name: tag.name }));

const getWorkspaceTags = (rootWorkspace: ArdoqId): APITagAttributes[] =>
  getAllTags().filter(tag => tag.rootWorkspace === rootWorkspace);

const entityHasTag = (
  tagName: string,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityId: ArdoqId
): boolean => {
  const attributeName =
    entityType === APIEntityType.COMPONENT ? 'components' : 'references';
  return getAllTags().some(
    tag =>
      tag.name === tagName &&
      Boolean(tag[attributeName]?.some((id: string) => id === entityId))
  );
};

/**
 * `tagInterface` is a Backbone-era singleton: every member snapshots
 * `tags$.state` at call time.
 *
 * The app is stream-based now. Join `tags$` (`streams/tags/tags$.ts`) at a
 * permitted boundary -- a `*$.ts` file, a file inside a `*$/` folder, or a
 * `*routines.ts` -- and thread the resolved tags downstream as plain parameters.
 * The pure reads live in `tagUtils` (`@ardoq/scope-data`); `tagOperations`
 * (`@ardoq/core`) covers the tag *edit* side (`parseTagInput`, `diffTagNames`,
 * `buildTagIntents`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const tagInterface: TagInterfaceImplementation = {
  /**
   * @deprecated Read `tags$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getWorkspaceTags,
  /**
   * @deprecated Read `tags$.models`.
   */
  getAllTags,
  /**
   * @deprecated Call `tagUtils.getIdsOfComponentsWithTag` / `tagUtils.getIdsOfReferencesWithTag` (`@ardoq/scope-data`) with tags from `tags$`.
   */
  entityHasTag,
  /**
   * @deprecated Read `tags$.models` and select with `tagUtils.getAttributes` (`@ardoq/scope-data`).
   */
  getTagsForModel,
  /**
   * @deprecated Read `tags$.byId[id]`, or call `tagUtils.getTag` (`@ardoq/scope-data`).
   */
  get,
  /**
   * @deprecated Read `tags$.models.length`.
   */
  getTagCount,
};
